import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { 
  Calendar, Clock, Search, Download, User,
  Sun, StickyNote, ClipboardList, Timer, CalendarDays
} from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import Teacherheader from "../Teacherheader";
import Teachersidebar from "../Teachersidebar";
import { useSelector } from 'react-redux';
import { selectUsers } from '../../State/usersSlice';

interface DutyAssignment {
  dutyId: string;
  dutyName: string;
  day: string;
  date?: string;
  timeSlot: string;
  teachers: string[];
  notes?: string;
  description?: string;
  location?: string;
}

interface Roster {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  assignments: DutyAssignment[];
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const DUTY_COLORS: Record<string, string> = {
  'Morning Gate Duty': 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700',
  'Playground Duty': 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700',
  'Corridor Supervision': 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700',
  'Canteen Duty': 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700',
  'Bus Duty': 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700',
  'Assembly Supervision': 'bg-pink-100 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700',
};

const TeacherDutyRosterViewer = () => {
  const { currentUser } = useSelector(selectUsers);
  const teacherName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || (currentUser.email || "Staff Member") : "Staff Member";
  
  const [activeTab, setActiveTab] = useState("today");
  const [roster, setRoster] = useState<Roster | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [personalNotes, setPersonalNotes] = useState<Record<string, string>>({});
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [currentNoteKey, setCurrentNoteKey] = useState("");
  const [currentNoteValue, setCurrentNoteValue] = useState("");
  const [showDutyDetails, setShowDutyDetails] = useState<DutyAssignment | null>(null);

  // Load saved data
  useEffect(() => {
    const savedNotes = localStorage.getItem('dutyViewer_personalNotes');
    const savedRoster = localStorage.getItem('dutyViewer_roster');
    
    if (savedNotes) setPersonalNotes(JSON.parse(savedNotes));
    if (savedRoster) setRoster(JSON.parse(savedRoster));
  }, []);

  const getCurrentDay = () => {
    const day = new Date().getDay();
    return day === 0 ? 'Sunday' : day === 6 ? 'Saturday' : DAYS_OF_WEEK[day - 1];
  };

  const getTodaysDuties = (): DutyAssignment[] => {
    if (!roster) return [];
    const today = getCurrentDay();
    return roster.assignments.filter(a => a.day === today);
  };

  const getMyDuties = (): DutyAssignment[] => {
    if (!roster) return [];
    const nameNormalized = teacherName.toLowerCase().trim();
    return roster.assignments.filter(a => 
      a.teachers.some(t => 
        t.toLowerCase().includes(nameNormalized) || 
        nameNormalized.includes(t.toLowerCase())
      )
    );
  };

  // Save notes to localStorage
  useEffect(() => {
    localStorage.setItem('dutyViewer_personalNotes', JSON.stringify(personalNotes));
  }, [personalNotes]);


  const loadSampleData = () => {


    
    const sampleRoster: Roster = {
      id: '1',
      name: 'Term 1 2026',
      startDate: '2026-01-05',
      endDate: '2026-03-20',
      assignments: [
        { dutyId: '1', dutyName: 'Morning Gate Duty', day: 'Monday', timeSlot: '7:30 - 8:00 AM', teachers: ['JS', 'MJ'], notes: 'Wear hi-vis vest', description: 'Greet parents, monitor student entry, check uniforms', location: 'Main Gate' },
        { dutyId: '1', dutyName: 'Morning Gate Duty', day: 'Tuesday', timeSlot: '7:30 - 8:00 AM', teachers: ['RW', 'SB'], notes: '', description: 'Greet parents, monitor student entry, check uniforms', location: 'Main Gate' },
        { dutyId: '1', dutyName: 'Morning Gate Duty', day: 'Wednesday', timeSlot: '7:30 - 8:00 AM', teachers: ['JD', 'MW'], notes: '', description: 'Greet parents, monitor student entry, check uniforms', location: 'Main Gate' },
        { dutyId: '1', dutyName: 'Morning Gate Duty', day: 'Thursday', timeSlot: '7:30 - 8:00 AM', teachers: ['ET', 'JS'], notes: '', description: 'Greet parents, monitor student entry, check uniforms', location: 'Main Gate' },
        { dutyId: '1', dutyName: 'Morning Gate Duty', day: 'Friday', timeSlot: '7:30 - 8:00 AM', teachers: ['MJ', 'RW'], notes: '', description: 'Greet parents, monitor student entry, check uniforms', location: 'Main Gate' },
        { dutyId: '2', dutyName: 'Playground Duty', day: 'Monday', timeSlot: '10:30 - 11:00 AM', teachers: ['SB', 'JD', 'MW'], notes: 'Bring first aid kit', description: 'Monitor students during morning break, ensure safety', location: 'Main Playground' },
        { dutyId: '2', dutyName: 'Playground Duty', day: 'Tuesday', timeSlot: '10:30 - 11:00 AM', teachers: ['ET', 'JS', 'MJ'], notes: '', description: 'Monitor students during morning break, ensure safety', location: 'Main Playground' },
        { dutyId: '2', dutyName: 'Playground Duty', day: 'Wednesday', timeSlot: '10:30 - 11:00 AM', teachers: ['RW', 'SB', 'JD'], notes: '', description: 'Monitor students during morning break, ensure safety', location: 'Main Playground' },
        { dutyId: '2', dutyName: 'Playground Duty', day: 'Thursday', timeSlot: '10:30 - 11:00 AM', teachers: ['MW', 'ET', 'JS'], notes: '', description: 'Monitor students during morning break, ensure safety', location: 'Main Playground' },
        { dutyId: '2', dutyName: 'Playground Duty', day: 'Friday', timeSlot: '10:30 - 11:00 AM', teachers: ['MJ', 'RW', 'SB'], notes: '', description: 'Monitor students during morning break, ensure safety', location: 'Main Playground' },
        { dutyId: '3', dutyName: 'Corridor Supervision', day: 'Monday', timeSlot: '12:00 - 12:30 PM', teachers: ['ET', 'MW'], notes: '', description: 'Patrol hallways between classes, maintain order', location: 'Main Building Corridors' },
        { dutyId: '3', dutyName: 'Corridor Supervision', day: 'Tuesday', timeSlot: '12:00 - 12:30 PM', teachers: ['JS', 'SB'], notes: '', description: 'Patrol hallways between classes, maintain order', location: 'Main Building Corridors' },
        { dutyId: '3', dutyName: 'Corridor Supervision', day: 'Wednesday', timeSlot: '12:00 - 12:30 PM', teachers: ['MJ', 'ET'], notes: '', description: 'Patrol hallways between classes, maintain order', location: 'Main Building Corridors' },
        { dutyId: '3', dutyName: 'Corridor Supervision', day: 'Thursday', timeSlot: '12:00 - 12:30 PM', teachers: ['RW', 'JD'], notes: '', description: 'Patrol hallways between classes, maintain order', location: 'Main Building Corridors' },
        { dutyId: '3', dutyName: 'Corridor Supervision', day: 'Friday', timeSlot: '12:00 - 12:30 PM', teachers: ['MW', 'JS'], notes: '', description: 'Patrol hallways between classes, maintain order', location: 'Main Building Corridors' },
        { dutyId: '4', dutyName: 'Canteen Duty', day: 'Monday', timeSlot: '12:30 - 1:30 PM', teachers: ['RW', 'JD'], notes: '', description: 'Supervise lunch area, ensure orderly queuing', location: 'School Canteen' },
        { dutyId: '4', dutyName: 'Canteen Duty', day: 'Tuesday', timeSlot: '12:30 - 1:30 PM', teachers: ['MW', 'ET'], notes: '', description: 'Supervise lunch area, ensure orderly queuing', location: 'School Canteen' },
        { dutyId: '4', dutyName: 'Canteen Duty', day: 'Wednesday', timeSlot: '12:30 - 1:30 PM', teachers: ['JS', 'RW'], notes: '', description: 'Supervise lunch area, ensure orderly queuing', location: 'School Canteen' },
        { dutyId: '4', dutyName: 'Canteen Duty', day: 'Thursday', timeSlot: '12:30 - 1:30 PM', teachers: ['MJ', 'SB'], notes: '', description: 'Supervise lunch area, ensure orderly queuing', location: 'School Canteen' },
        { dutyId: '4', dutyName: 'Canteen Duty', day: 'Friday', timeSlot: '12:30 - 1:30 PM', teachers: ['JD', 'ET'], notes: '', description: 'Supervise lunch area, ensure orderly queuing', location: 'School Canteen' },
        { dutyId: '5', dutyName: 'Bus Duty', day: 'Monday', timeSlot: '3:00 - 3:30 PM', teachers: ['MJ', 'SB'], notes: '', description: 'Assist with student dismissal, bus loading supervision', location: 'Bus Bay' },
        { dutyId: '5', dutyName: 'Bus Duty', day: 'Tuesday', timeSlot: '3:00 - 3:30 PM', teachers: ['JD', 'RW'], notes: '', description: 'Assist with student dismissal, bus loading supervision', location: 'Bus Bay' },
        { dutyId: '5', dutyName: 'Bus Duty', day: 'Wednesday', timeSlot: '3:00 - 3:30 PM', teachers: ['MW', 'MJ'], notes: '', description: 'Assist with student dismissal, bus loading supervision', location: 'Bus Bay' },
        { dutyId: '5', dutyName: 'Bus Duty', day: 'Thursday', timeSlot: '3:00 - 3:30 PM', teachers: ['ET', 'JD'], notes: '', description: 'Assist with student dismissal, bus loading supervision', location: 'Bus Bay' },
        { dutyId: '5', dutyName: 'Bus Duty', day: 'Friday', timeSlot: '3:00 - 3:30 PM', teachers: ['SB', 'MW'], notes: '', description: 'Assist with student dismissal, bus loading supervision', location: 'Bus Bay' },
        { dutyId: '6', dutyName: 'Assembly Supervision', day: 'Monday', timeSlot: '8:00 - 8:30 AM', teachers: ['JS', 'MJ', 'RW', 'SB'], notes: 'Full assembly day', description: 'Manage students during morning assembly, maintain discipline', location: 'Main Hall' },
        { dutyId: '6', dutyName: 'Assembly Supervision', day: 'Friday', timeSlot: '8:00 - 8:30 AM', teachers: ['JD', 'MW', 'ET', 'JS'], notes: 'Awards assembly', description: 'Manage students during morning assembly, maintain discipline', location: 'Main Hall' },
      ]
    };

    setRoster(sampleRoster);
    localStorage.setItem('dutyViewer_roster', JSON.stringify(sampleRoster));
    toast({ title: "Sample Data Loaded", description: "Duty roster with sample assignments is ready." });
  };


  const isMyDuty = (duty: DutyAssignment): boolean => {
    const nameNormalized = teacherName.toLowerCase().trim();
    return duty.teachers.some(t => 
      t.toLowerCase().includes(nameNormalized) || 
      nameNormalized.includes(t.toLowerCase())
    );
  };

  const getNextDuty = (): DutyAssignment | null => {
    const myDuties = getMyDuties();
    const today = getCurrentDay();
    const todayIndex = DAYS_OF_WEEK.indexOf(today);
    
    // Sort by day order from today
    const sortedDuties = myDuties.sort((a, b) => {
      const aIndex = DAYS_OF_WEEK.indexOf(a.day);
      const bIndex = DAYS_OF_WEEK.indexOf(b.day);
      const aOffset = aIndex >= todayIndex ? aIndex - todayIndex : aIndex + 7 - todayIndex;
      const bOffset = bIndex >= todayIndex ? bIndex - todayIndex : bIndex + 7 - todayIndex;
      return aOffset - bOffset;
    });

    return sortedDuties[0] || null;
  };

  const getCountdownText = (duty: DutyAssignment): string => {
    const today = getCurrentDay();
    const todayIndex = DAYS_OF_WEEK.indexOf(today);
    const dutyIndex = DAYS_OF_WEEK.indexOf(duty.day);
    
    if (duty.day === today) return "Today";
    
    let daysUntil = dutyIndex - todayIndex;
    if (daysUntil <= 0) daysUntil += 7;
    
    return daysUntil === 1 ? "Tomorrow" : `In ${daysUntil} days`;
  };

  const searchDuties = (): DutyAssignment[] => {
    if (!roster || !searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return roster.assignments.filter(a => 
      a.dutyName.toLowerCase().includes(query) ||
      a.teachers.some(t => t.toLowerCase().includes(query)) ||
      a.day.toLowerCase().includes(query) ||
      a.location?.toLowerCase().includes(query)
    );
  };

  const savePersonalNote = () => {
    setPersonalNotes({ ...personalNotes, [currentNoteKey]: currentNoteValue });
    setShowNoteDialog(false);
    toast({ title: "Note Saved" });
  };

  const exportToCalendar = () => {
    const myDuties = getMyDuties();
    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//School Duty Roster//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
`;

    myDuties.forEach((duty, index) => {
      const dayOffset = DAYS_OF_WEEK.indexOf(duty.day);
      const nextDate = new Date();
      const currentDayOffset = nextDate.getDay() === 0 ? 6 : nextDate.getDay() - 1;
      let daysToAdd = dayOffset - currentDayOffset;
      if (daysToAdd < 0) daysToAdd += 7;
      nextDate.setDate(nextDate.getDate() + daysToAdd);
      
      const dateStr = nextDate.toISOString().split('T')[0].replace(/-/g, '');
      
      icsContent += `BEGIN:VEVENT
UID:duty-${index}-${Date.now()}@school
DTSTART:${dateStr}T073000
DTEND:${dateStr}T080000
SUMMARY:${duty.dutyName}
DESCRIPTION:${duty.description || ''} ${duty.notes ? '- Note: ' + duty.notes : ''}
LOCATION:${duty.location || ''}
END:VEVENT
`;
    });

    icsContent += `END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my_duties_${teacherName.replace(/\s+/g, '_')}.ics`;
    a.click();
    toast({ title: "Calendar Exported", description: "Your duties have been exported to an iCal file." });
  };

  const getDutyColor = (dutyName: string): string => {
    return DUTY_COLORS[dutyName] || 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600';
  };


  const todaysDuties = getTodaysDuties();
  const myDuties = getMyDuties();
  const nextDuty = getNextDuty();
  const searchResults = searchDuties();

  return (
    <SidebarProvider>
      <div className="flex min-h-screen bg-gray-50/50 dark:bg-gray-900 w-full font-sans">
        <Teachersidebar />
        <main className="flex-1 flex flex-col w-full h-screen overflow-hidden">
          <Teacherheader />
          <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
            <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Duty Roster</h1>
          <p className="text-muted-foreground">Welcome, {teacherName}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={loadSampleData}>
            Load Sample
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCalendar}>
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      {/* Next Duty Banner */}
      {nextDuty && (
        <Card className={`border-2 ${isMyDuty(nextDuty) ? 'border-primary bg-primary/5' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Timer className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Your Next Duty</p>
                  <p className="font-semibold">{nextDuty.dutyName} - {nextDuty.day}</p>
                  <p className="text-sm text-muted-foreground">{nextDuty.timeSlot}</p>
                </div>
              </div>
              <Badge variant="default" className="text-lg px-3 py-1">
                {getCountdownText(nextDuty)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="myduties">My Duties</TabsTrigger>
          <TabsTrigger value="weekly">Weekly</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
        </TabsList>

        {/* Today's Duties */}
        <TabsContent value="today" className="space-y-4">
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="h-5 w-5 text-yellow-500" />
                Today's Duties - {getCurrentDay()}, {new Date().toLocaleDateString()}
              </CardTitle>
              <CardDescription>{todaysDuties.length} duties scheduled for today</CardDescription>
            </CardHeader>
            <CardContent>
              {!roster ? (
                <div className="text-center py-8">
                  <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No roster loaded. Load sample data or upload a JSON file.</p>
                  <Button className="mt-4" onClick={loadSampleData}>Load Sample Data</Button>
                </div>
              ) : todaysDuties.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No duties scheduled for today</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todaysDuties.sort((a, b) => a.timeSlot.localeCompare(b.timeSlot)).map((duty, idx) => (
                    <Card 
                      key={idx} 
                      className={`cursor-pointer transition-all hover:shadow-md ${getDutyColor(duty.dutyName)} ${isMyDuty(duty) ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => setShowDutyDetails(duty)}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{duty.dutyName}</h3>
                              {isMyDuty(duty) && <Badge variant="default">Your Duty</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {duty.timeSlot}
                            </p>
                            {duty.location && (
                              <p className="text-sm text-muted-foreground">{duty.location}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="flex flex-wrap gap-1 justify-end">
                              {duty.teachers.map((t, i) => (
                                <Badge 
                                  key={i} 
                                  variant={t.toLowerCase().includes(teacherName.toLowerCase().charAt(0)) ? "default" : "secondary"}
                                >
                                  {t}
                                </Badge>
                              ))}
                            </div>
                            {duty.notes && (
                              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">📝 {duty.notes}</p>
                            )}
                          </div>
                        </div>
                        {personalNotes[`${duty.dutyId}-${duty.day}`] && (
                          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-sm">
                            <StickyNote className="h-3 w-3 inline mr-1" />
                            {personalNotes[`${duty.dutyId}-${duty.day}`]}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Duties */}
        <TabsContent value="myduties" className="space-y-4">
          <Card className="bg-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    My Duties
                  </CardTitle>
                  <CardDescription>{myDuties.length} duties assigned to you this week</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={exportToCalendar}>
                  <CalendarDays className="h-4 w-4 mr-1" /> Add to Calendar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {myDuties.length === 0 ? (
                <div className="text-center py-8">
                  <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No duties assigned to you</p>
                  <p className="text-sm text-muted-foreground mt-1">Make sure your name matches the roster entries</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {DAYS_OF_WEEK.map(day => {
                    const dayDuties = myDuties.filter(d => d.day === day);
                    if (dayDuties.length === 0) return null;
                    
                    return (
                      <div key={day}>
                        <h4 className="font-medium text-sm text-muted-foreground mb-2 flex items-center gap-2">
                          {day}
                          {day === getCurrentDay() && <Badge variant="default" className="text-xs">Today</Badge>}
                        </h4>
                        <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                          {dayDuties.sort((a, b) => a.timeSlot.localeCompare(b.timeSlot)).map((duty, idx) => (
                            <Card 
                              key={idx} 
                              className={`cursor-pointer hover:shadow-md transition-all ${getDutyColor(duty.dutyName)}`}
                              onClick={() => setShowDutyDetails(duty)}
                            >
                              <CardContent className="p-3">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium">{duty.dutyName}</p>
                                    <p className="text-sm text-muted-foreground">{duty.timeSlot} • {duty.location}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex gap-1">
                                      {duty.teachers.filter(t => !t.toLowerCase().includes(teacherName.toLowerCase().charAt(0))).map((t, i) => (
                                        <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                                      ))}
                                    </div>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCurrentNoteKey(`${duty.dutyId}-${duty.day}`);
                                        setCurrentNoteValue(personalNotes[`${duty.dutyId}-${duty.day}`] || '');
                                        setShowNoteDialog(true);
                                      }}
                                    >
                                      <StickyNote className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                {personalNotes[`${duty.dutyId}-${duty.day}`] && (
                                  <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs">
                                    {personalNotes[`${duty.dutyId}-${duty.day}`]}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly View */}
        <TabsContent value="weekly" className="space-y-4">
          <Card className="bg-card overflow-x-auto">
            <CardHeader>
              <CardTitle>Weekly Roster</CardTitle>
              <CardDescription>Full duty schedule for the week</CardDescription>
            </CardHeader>
            <CardContent>
              {!roster ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No roster loaded</p>
                </div>
              ) : (
                <div className="min-w-[600px]">
                  <div className="grid grid-cols-6 gap-2">
                    <div className="font-medium p-2 bg-muted rounded">Duty</div>
                    {DAYS_OF_WEEK.map(day => (
                      <div 
                        key={day} 
                        className={`font-medium p-2 text-center rounded ${day === getCurrentDay() ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                      >
                        {day.slice(0, 3)}
                      </div>
                    ))}
                  </div>
                  
                  {/* Get unique duty types */}
                  {[...new Set(roster.assignments.map(a => a.dutyName))].map(dutyName => (
                    <div key={dutyName} className="grid grid-cols-6 gap-2 mt-2">
                      <div className="p-2 text-sm font-medium">{dutyName}</div>
                      {DAYS_OF_WEEK.map(day => {
                        const duty = roster.assignments.find(a => a.dutyName === dutyName && a.day === day);
                        const isMine = duty && isMyDuty(duty);
                        
                        return (
                          <div 
                            key={day} 
                            className={`p-2 rounded text-xs cursor-pointer transition-all hover:shadow ${duty ? getDutyColor(dutyName) : 'bg-muted/30'} ${isMine ? 'ring-2 ring-primary' : ''}`}
                            onClick={() => duty && setShowDutyDetails(duty)}
                          >
                            {duty ? (
                              <div className="flex flex-wrap gap-1">
                                {duty.teachers.map((t, i) => (
                                  <Badge 
                                    key={i} 
                                    variant={t.toLowerCase().includes(teacherName.toLowerCase().charAt(0)) ? "default" : "outline"}
                                    className="text-xs"
                                  >
                                    {t}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Search */}
        <TabsContent value="search" className="space-y-4">
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Search Duties
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by teacher, duty, day, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {searchQuery && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{searchResults.length} results found</p>
                  {searchResults.map((duty, idx) => (
                    <Card 
                      key={idx} 
                      className={`cursor-pointer hover:shadow-md ${getDutyColor(duty.dutyName)} ${isMyDuty(duty) ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => setShowDutyDetails(duty)}
                    >
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{duty.dutyName}</p>
                            <p className="text-sm text-muted-foreground">{duty.day} • {duty.timeSlot}</p>
                            {duty.location && <p className="text-sm text-muted-foreground">{duty.location}</p>}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {duty.teachers.map((t, i) => (
                              <Badge key={i} variant="secondary">{t}</Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Quick lookups */}
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-3">Quick Lookups</h4>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSearchQuery('Monday')}>
                    Monday
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSearchQuery('Gate')}>
                    Gate Duty
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSearchQuery('Playground')}>
                    Playground
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSearchQuery('Bus')}>
                    Bus Duty
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Duty Details Modal */}
      <Dialog open={!!showDutyDetails} onOpenChange={() => setShowDutyDetails(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{showDutyDetails?.dutyName}</DialogTitle>
          </DialogHeader>
          {showDutyDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Day</Label>
                  <p className="font-medium">{showDutyDetails.day}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Time</Label>
                  <p className="font-medium">{showDutyDetails.timeSlot}</p>
                </div>
              </div>
              
              {showDutyDetails.location && (
                <div>
                  <Label className="text-muted-foreground">Location</Label>
                  <p className="font-medium">{showDutyDetails.location}</p>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground">Assigned Teachers</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {showDutyDetails.teachers.map((t, i) => (
                    <Badge key={i} variant={isMyDuty(showDutyDetails) && t.toLowerCase().includes(teacherName.toLowerCase().charAt(0)) ? "default" : "secondary"}>
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>

              {showDutyDetails.description && (
                <div>
                  <Label className="text-muted-foreground">Responsibilities</Label>
                  <p className="text-sm">{showDutyDetails.description}</p>
                </div>
              )}

              {showDutyDetails.notes && (
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded">
                  <Label className="text-orange-700 dark:text-orange-400">Notes</Label>
                  <p className="text-sm">{showDutyDetails.notes}</p>
                </div>
              )}

              {isMyDuty(showDutyDetails) && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setCurrentNoteKey(`${showDutyDetails.dutyId}-${showDutyDetails.day}`);
                    setCurrentNoteValue(personalNotes[`${showDutyDetails.dutyId}-${showDutyDetails.day}`] || '');
                    setShowNoteDialog(true);
                    setShowDutyDetails(null);
                  }}
                >
                  <StickyNote className="h-4 w-4 mr-2" />
                  Add Personal Note
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Personal Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Personal Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea 
              value={currentNoteValue}
              onChange={(e) => setCurrentNoteValue(e.target.value)}
              placeholder="Add a personal reminder (e.g., Bring whistle, Check equipment)..."
              rows={4}
            />
            <div className="flex gap-2">
              <Button onClick={savePersonalNote} className="flex-1">Save Note</Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setPersonalNotes({ ...personalNotes, [currentNoteKey]: '' });
                  setShowNoteDialog(false);
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default TeacherDutyRosterViewer;